import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  canonicalUrl?: string;
  type?: "website" | "product" | "article";
  product?: {
    price: number;
    currency?: string;
    availability?: "in stock" | "out of stock";
  };
  jsonLd?: Record<string, any> | Record<string, any>[];
}

export function SEOHead({
  title,
  description,
  keywords,
  image = "https://lovable.dev/opengraph-image-p98pqg.png",
  url,
  canonicalUrl,
  type = "website",
  product,
  jsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    // Update document title
    document.title = `${title} | Leestone`;

    // Update or create meta tags
    const updateMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update or create link tags  
    const updateLink = (rel: string, href: string, hreflang?: string) => {
      const selector = hreflang 
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
      let link = document.querySelector(selector) as HTMLLinkElement;
      if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        if (hreflang) link.hreflang = hreflang;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    updateMeta("description", description);
    if (keywords) updateMeta("keywords", keywords);
    
    // Canonical URL
    const canonical = canonicalUrl || url || window.location.href;
    updateLink("canonical", canonical);
    
    // Hreflang for future i18n support
    updateLink("alternate", canonical, "en");
    updateLink("alternate", canonical, "x-default");
    
    // Open Graph
    updateMeta("og:title", title, true);
    updateMeta("og:description", description, true);
    updateMeta("og:type", type, true);
    updateMeta("og:image", image, true);
    if (url) updateMeta("og:url", url, true);

    // Twitter
    updateMeta("twitter:title", title);
    updateMeta("twitter:description", description);
    updateMeta("twitter:image", image);

    // Product structured data
    if (product) {
      let script = document.querySelector('script[data-seo="product"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo", "product");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        name: title,
        description: description,
        image: image,
        offers: {
          "@type": "Offer",
          price: product.price,
          priceCurrency: product.currency || "INR",
          availability: product.availability === "in stock" 
            ? "https://schema.org/InStock" 
            : "https://schema.org/OutOfStock",
        },
      });
    }

    // Generic JSON-LD
    if (jsonLd) {
      let script = document.querySelector('script[data-seo="json-ld"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo", "json-ld");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      // Cleanup structured data on unmount
      const productScript = document.querySelector('script[data-seo="product"]');
      if (productScript) productScript.remove();
      
      const jsonLdScript = document.querySelector('script[data-seo="json-ld"]');
      if (jsonLdScript) jsonLdScript.remove();
    };
  }, [title, description, keywords, image, url, canonicalUrl, type, product, jsonLd]); // Added jsonLd to dependencies

  return null;
}

